import * as React from "react";
import MultiStepForm from "./commons/components/form/MultiStepForm";
import SubscriptionPlanFull from "./commons/model/pricing/SubscriptionPlanFull";
import PaymentFormWrapper from "./registration/PaymentFormWrapper";
import InputState from "./commons/model/form/InputState";
import StripeError from "./commons/model/pricing/StripeError";
import sfetch from "./commons/utils/super-fetch";
import PersonalDetailsForm from "./registration/PersonalDetailsForm";
import PracticeForm from "./registration/PracticeForm";
import { LegalConsent } from "./legal/LegalResource";
import { getPayload } from "./commons/utils/form-utils";
import Country from "./commons/model/address/Country";
import { createRoot } from "react-dom/client";

interface UserWithPracticeRegistrationFormProps {
  validateRelativeEndpoint: string;
  platformByCountry: Map<string, string>;
  consumeRelativeEndpoint: string;
  successRelativeEndpoint: string;
  loginRelativeEndpoint: string;
  countries: Country[];
  countriesForTelInput: Country[];
  publishableApiKey?: string;
  checkRegistrationSuccessRelativeEndpoint?: string;
  stripePlan?: SubscriptionPlanFull;
  contactUsEmail: String;
  legalConsent: LegalConsent;
  googleApiKey: string;
}

interface UserWithPracticeRegistrationFormState {
  currentStep: number;
  personalDetails: {};
  practice: {};
  payment: {};
  userConsent: {};
  errorMsg1?: string;
  errorMsg2?: string;
  country: Country;
  stripeError?: StripeError;
  checkSuccessEndpoint?: string;
  startTime?: number;
  username?: string;
  password?: string;
}

export default class UserWithPracticeRegistrationForm extends React.Component<
  UserWithPracticeRegistrationFormProps,
  UserWithPracticeRegistrationFormState
> {
  private readonly successPollingDelayMs = 2 * 1000;
  private readonly successPollingTimeoutMs = 5 * 60 * 1000;
  private masterPromiseResolve: (value: any) => void;
  private masterPromiseReject: () => any;

  constructor(props: UserWithPracticeRegistrationFormProps) {
    super(props);
    this.state = {
      currentStep: 0,
      personalDetails: null,
      practice: null,
      payment: null,
      userConsent: null,
      country: null,
      checkSuccessEndpoint: null,
      username: null,
      password: null,
    };
    this.onPersonalDetailsSubmitted = this.onPersonalDetailsSubmitted.bind(this);
    this.onPracticeSubmitted = this.onPracticeSubmitted.bind(this);
    this.onPaymentSubmitted = this.onPaymentSubmitted.bind(this);
    this.resetErrorAndSubmit = this.resetErrorAndSubmit.bind(this);
    this.submit = this.submit.bind(this);
    this.hasPaymentStep = this.hasPaymentStep.bind(this);
    this.checkRegistrationSuccess = this.checkRegistrationSuccess.bind(this);
    this.onPaymentRequired = this.onPaymentRequired.bind(this);
    this.onInternalError = this.onInternalError.bind(this);
    this.onBadRequest = this.onBadRequest.bind(this);
    this.onSuccess = this.onSuccess.bind(this);
    this.displayError = this.displayError.bind(this);
    this.getCheckSuccessEndpoint = this.getCheckSuccessEndpoint.bind(this);
    this.onLegalDocumentsLoadError = this.onLegalDocumentsLoadError.bind(this);
  }

  componentDidUpdate(
    prevProps: Readonly<UserWithPracticeRegistrationFormProps>,
    prevState: Readonly<UserWithPracticeRegistrationFormState>
  ): void {
    if (!prevState.checkSuccessEndpoint && this.state.checkSuccessEndpoint) {
      this.setState({ startTime: performance.now() }, this.scheduleCheckRegistrationSuccess);
    }
  }

  private scheduleCheckRegistrationSuccess() {
    if (
      this.state.startTime &&
      performance.now() - this.state.startTime < this.successPollingTimeoutMs
    ) {
      setTimeout(this.checkRegistrationSuccess, this.successPollingDelayMs);
    } else {
      this.masterPromiseReject && this.masterPromiseReject();
    }
  }

  private hasPaymentStep() {
    return this.props.stripePlan != null;
  }

  private checkRegistrationSuccess() {
    if (this.state.checkSuccessEndpoint) {
      sfetch(this.state.checkSuccessEndpoint, {
        method: "GET",
      })
        .then(() => this.onSuccess())
        .catch(() => this.scheduleCheckRegistrationSuccess());
    } else {
      this.scheduleCheckRegistrationSuccess();
    }
  }

  render() {
    const hasPaymentStep = this.hasPaymentStep();
    const legalConsent: LegalConsent = {
      legalResources: this.props.legalConsent.legalResources,
      onError: this.onLegalDocumentsLoadError,
    };
    let practiceSubmitLabel: string;
    if (hasPaymentStep) {
      practiceSubmitLabel = i18n["Global.Continue"];
    } else {
      practiceSubmitLabel = i18n["Global.Register"];
    }

    return (
      <MultiStepForm currentStep={this.state.currentStep}>
        <PersonalDetailsForm
          validateEndpoint={this.props.validateRelativeEndpoint}
          submitLabel={i18n["Global.Continue"]}
          onSubmitSucceeded={this.onPersonalDetailsSubmitted}
        />

        <PracticeForm
          validateEndpoint={this.props.validateRelativeEndpoint}
          countries={this.props.countries}
          submitLabel={practiceSubmitLabel}
          onSubmitSucceeded={this.onPracticeSubmitted}
          legalConsent={!hasPaymentStep ? legalConsent : null}
          displayTaxNumber={hasPaymentStep}
          errorMsg={this.state.errorMsg1}
          backLink={{
            label: i18n["Form.GenericRegistration.PersonalInformation.Title"].toLowerCase(),
            onClick: () => this.setState({ currentStep: 0 }),
          }}
          googleApiKey={this.props.googleApiKey}
        />

        {hasPaymentStep && (
          <PaymentFormWrapper
            stripePublishableApiKey={this.props.publishableApiKey}
            stripePlanId={this.props.stripePlan.id}
            priceExcludingTaxInCents={this.props.stripePlan.priceExcludingTaxInCents}
            currency={this.props.stripePlan.currency}
            billedInterval={this.props.stripePlan.billedInterval}
            trialPeriodDay={this.props.stripePlan.trialPeriodDay}
            taxRate={
              this.state.country ? this.props.stripePlan.taxRates[this.state.country.name] : null
            }
            country={this.state.country}
            validateEndpoint={this.props.validateRelativeEndpoint}
            submitLabel={i18n["Global.Subscribe"]}
            onSubmitSucceeded={this.onPaymentSubmitted}
            legalConsent={hasPaymentStep ? legalConsent : null}
            errorMsg={this.state.errorMsg2}
            stripeError={this.state.stripeError}
            contactUsEmail={this.props.contactUsEmail}
            backLink={{
              label: i18n["Form.DoctorRegistration.PracticeInformation.Title"].toLowerCase(),
              onClick: () => this.setState({ currentStep: 1 }),
            }}
          />
        )}
      </MultiStepForm>
    );
  }

  private onPersonalDetailsSubmitted(payload: any, form: { [key: string]: InputState }): void {
    this.setState({
      currentStep: 1,
      personalDetails: payload,
      username: form["email"].value,
      password: form["password"].value,
    });
  }

  private onPracticeSubmitted(payload: any, form: { [key: string]: InputState }): void {
    this.setState(
      {
        practice: payload,
        userConsent: getPayload(form, ["legalDocuments"]),
        country: this.props.countries.find(
          (it) => it.name == payload["practice"]["address"]["country"]
        ),
      },
      () => {
        if (this.hasPaymentStep()) {
          this.setState({
            currentStep: 2,
          });
        } else {
          //no payment step -> we submit
          this.resetErrorAndSubmit();
        }
      }
    );
  }

  private onPaymentSubmitted(payload: any, form: { [key: string]: InputState }): Promise<any> {
    return new Promise((resolve, reject) => {
      this.masterPromiseResolve = resolve;
      this.masterPromiseReject = reject;
      this.setState(
        {
          payment: payload,
          userConsent: getPayload(form, ["legalDocuments"]),
        },
        () => this.resetErrorAndSubmit()
      );
    });
  }

  private onSuccess() {
    this.masterPromiseResolve && this.masterPromiseResolve(true);
    document.location.href =
      this.props.platformByCountry[this.state.country.name] + this.props.successRelativeEndpoint;
  }

  private resetErrorAndSubmit() {
    this.setState(
      {
        stripeError: null,
        checkSuccessEndpoint: null,
        errorMsg1: null,
        errorMsg2: null,
      },
      () => this.submit()
    );
  }

  private submit(): Promise<any> {
    const request = Object.assign(
      {},
      this.state.personalDetails,
      this.state.practice,
      this.state.payment,
      { userConsent: this.state.userConsent }
    );
    const endpoint =
      this.props.platformByCountry[this.state.country.name] + this.props.consumeRelativeEndpoint;
    return sfetch(endpoint, {
      method: "POST",
      body: JSON.stringify(request),
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        response.json().then((json) => {
          const token = json["registrationToken"];
          const paymentStatus = json["payment"]["status"];
          if (!token || !paymentStatus) {
            this.onInternalError();
          } else if (paymentStatus === "SUCCESS") {
            if (json["isRegistered"] === true || json["registered"] === true) {
              //blaborde 12082019: jackson removes the `is` prefix
              this.onSuccess();
            } else {
              //wait for registration
              if (this.props.stripePlan != null) {
                this.setState({
                  checkSuccessEndpoint: this.getCheckSuccessEndpoint(token),
                });
              }
            }
          } else if (paymentStatus === "REQUIRES_ACTION") {
            if (this.props.stripePlan != null) {
              this.setState({
                stripeError: new StripeError(json["payment"], this.masterPromiseReject),
                checkSuccessEndpoint: this.getCheckSuccessEndpoint(token),
              });
            }
          } else {
            this.onInternalError();
          }
        });
      })
      .catch((response) => {
        if (response.status == 402) {
          this.onPaymentRequired(response);
        } else if (response.status == 400) {
          this.onBadRequest();
        } else {
          this.onInternalError();
        }
      });
  }

  private getCheckSuccessEndpoint(token: string) {
    return `${this.props.platformByCountry[this.state.country.name]}${
      this.props.checkRegistrationSuccessRelativeEndpoint
    }`.replace("{registrationToken}", token);
  }

  private onPaymentRequired(response) {
    response.json().then((json) => {
      this.setState({
        stripeError: new StripeError(json, this.masterPromiseReject),
      });
    });
  }

  private onLegalDocumentsLoadError() {
    this.displayError(i18n["Legal.Consent.Error"]);
  }

  private onBadRequest() {
    this.displayError(i18n["Form.DoctorRegistration.Error.BadRequest"]);
    this.masterPromiseReject && this.masterPromiseReject();
  }

  private onInternalError() {
    this.displayError(i18n["Form.DoctorRegistration.Error.Internal"]);
    this.masterPromiseReject && this.masterPromiseReject();
  }

  private displayError(msg: string) {
    const hasPaymentStep = this.hasPaymentStep();
    this.setState({
      currentStep: hasPaymentStep ? 2 : 1,
      errorMsg1: hasPaymentStep ? null : msg,
      errorMsg2: hasPaymentStep ? msg : null,
    });
  }

  static loadComponent(elementId: string, props: UserWithPracticeRegistrationFormProps) {
    const container = document.getElementById(elementId);
    const root = createRoot(container!);
    root.render(<UserWithPracticeRegistrationForm {...props} />);
  }
}
